import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogModule,
} from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import * as Hammer from 'hammerjs';
import { cloneDeep } from 'lodash';
import { Observable, Subject, filter, map, withLatestFrom } from 'rxjs';

import {
  clearGameHistory,
  getAllGameHistory,
  getGameHistoryLoaded,
  getGameHistoryTotal,
  loadGameHistory,
} from '@fantaz/game-history/data-access';
import { GameHistoryUiHistoryListTableModule } from '@fantaz/game-history/ui/history-list-table';
import { env, environment } from '@fantaz/shared/app-config';
import * as fromAuth from '@fantaz/shared/auth';
import {
  AuthServiceInjectionToken,
  FzGoogleTagManagerService,
  MatchMakingService,
  TournamentApiService,
} from '@fantaz/shared/data-access/api';
import {
  BestScores,
  BuyPassRequest,
  CasualTournamentDetails,
  ConfiguredPasses,
  CurrentUser,
  EventSchedule,
  Game,
  GoogleAnalyticsEvent,
  Leader,
  MatchResultsForUser,
  PrizeDetails,
  TournamentPass,
  TournamentTagsEnum,
  UserPasses,
} from '@fantaz/shared/data-access/models';
import { ResponsiveTableModule } from '@fantaz/shared/directives/responsive-table';
import { BreadcrumbsService } from '@fantaz/shared/layout/breadcrumbs';
import { PrizeImagePipe, SharedPipesModule } from '@fantaz/shared/pipes';
import { SharedPipesDateModule } from '@fantaz/shared/pipes/date';
import { ShareService } from '@fantaz/shared/share';
import { SharedUiCardModule } from '@fantaz/shared/ui/card';
import { SharedUiConditionalModule } from '@fantaz/shared/ui/conditional';
import { CurrencyModule } from '@fantaz/shared/ui/currency';
import { EmptyListModule } from '@fantaz/shared/ui/empty-list';
import { GamesUiGameDetailsModule } from '@fantaz/shared/ui/game-details';
import { SharedUiLoadingComponentModule } from '@fantaz/shared/ui/loading-component';
import { MaterialModule } from '@fantaz/shared/ui/material';
import { ModalAlertComponent } from '@fantaz/shared/ui/modal-alert';
import { SharedUiRouterTabsModule } from '@fantaz/shared/ui/router-tabs';
import { SharedUiTableComponentModule } from '@fantaz/shared/ui/table-component';
import { CurrencyService } from '@fantaz/shared/utils';
import * as fromCasualTournamentDetails from '@fantaz/tournaments/data-access';
import {
  LeaderState,
  PassesFacade,
  PlayTournamentFacade,
  TournamentDataAccessModule,
  clearPasses,
  getAllLeaderBoard,
  getLeaderBoardLoaded,
  getLeaderBoardLoading,
  getTotalLeaderBoard,
  isPlayReady,
  loadLeaderBoard,
  loadSubscriptionTournament,
  nextEventsQuery,
} from '@fantaz/tournaments/data-access';
import { GameCardModule } from '@fantaz/tournaments/ui/game-card';
import {
  MatchMakingDialogComponent,
  TournamentsUiMatchMakingDialogModule,
} from '@fantaz/tournaments/ui/match-making-dialog';
import { PageTabRoute } from '@fantaz/tournaments/ui/page-tab-route';
import { TournamentsUiPlayControlsModule } from '@fantaz/tournaments/ui/play-controls';
import { TournamentsUiScoreSummaryModule } from '@fantaz/tournaments/ui/score-summary';
import { TournamentBannerModule } from '@fantaz/tournaments/ui/tournament-banner';
import { TournamentControlsModule } from '@fantaz/tournaments/ui/tournament-controls';

import { TournamentDetails } from './details/tournament-details';
import { UserPlayHistory } from './history/user-play-history';
import { TournamentLeaderboard } from './leaderboard/tournament-leaderboard';
import { OpenEventsTabComponent } from './open-events/open-events';
import { TournamentOverview } from './overview/tournament-overview';
import { TournamentRules } from './rules/tournament-rules';

type BannerImages = {
  image: string;
  sm?: string;
  md?: string;
  lg?: string;
};

@UntilDestroy()
@Component({
  selector: 'fz-tournament-details-feature',
  standalone: true,
  imports: [
    CommonModule,
    SharedUiConditionalModule,
    CurrencyModule,
    TournamentsUiMatchMakingDialogModule,
    MatButtonModule,
    TournamentBannerModule,
    MatDialogModule,
    ResponsiveTableModule,
    GamesUiGameDetailsModule,
    MaterialModule,
    SharedPipesModule,
    SharedUiRouterTabsModule,
    TournamentsUiScoreSummaryModule,
    TournamentsUiPlayControlsModule,
    EmptyListModule,
    GameHistoryUiHistoryListTableModule,
    TournamentControlsModule,
    SharedPipesDateModule,
    SharedUiLoadingComponentModule,
    TournamentDataAccessModule,
    GameCardModule,
    SharedPipesModule,
    SharedUiCardModule,
    SharedUiTableComponentModule,
    OpenEventsTabComponent,
    TournamentOverview,
    TournamentLeaderboard,
    TournamentRules,
    TournamentDetails,
    UserPlayHistory,
    PrizeImagePipe,
  ],
  templateUrl: './tournament-details-feature.html',
  styleUrls: ['./tournament-details-feature.scss'],
  animations: [
    trigger('slideLeftAnimation', [
      transition(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0.5 }),
        animate(
          '300ms cubic-bezier(0.23, 1, 0.32, 1)',
          style({ transform: 'translateX(0)', opacity: 1 }),
        ),
      ]),
      transition(':leave', [
        style({ transform: 'translateX(0)', opacity: 1 }),
        animate(
          '300ms cubic-bezier(0.23, 1, 0.32, 1)',
          style({ transform: 'translateX(100%)', opacity: 0 }),
        ),
      ]),
    ]),
  ],
})
export class TournamentDetailsFeatures implements OnInit, OnDestroy {
  #authService = inject(AuthServiceInjectionToken);
  #playFacade = inject(PlayTournamentFacade);
  #currency = inject(CurrencyService);

  loadingTournamentDetails$: Observable<boolean> = this.store.select(
    fromCasualTournamentDetails.getCasualTournamentDetailsLoaded,
  );
  tournamentDetails: CasualTournamentDetails | null;
  unsubscribe$: Subject<void> = new Subject<void>();
  user: CurrentUser | null | undefined;
  gameHistory$: Observable<MatchResultsForUser[]> = new Observable();
  totalGameHistory$: Observable<number> = new Observable();
  width: number;
  minWith = 600;
  selectedTab: number;
  loggedIn: boolean;

  /**
   * Subscribe  to tournament details
   */
  casualTournamentDetails$: Observable<CasualTournamentDetails | null> =
    this.store
      .select(fromCasualTournamentDetails.getCasualTournamentDetails)
      .pipe(
        filter((details) => details !== null),
        untilDestroyed(this),
      );
  timesUserTournamentPlayed$: Observable<number> = this.store
    .select(fromCasualTournamentDetails.getTimesUserTournamentDetailsPlayed)
    .pipe(untilDestroyed(this));
  tournamentId = '';
  eventId = '';
  currentEventSchedule$: Observable<EventSchedule | null | undefined>;
  eventSchedulePrizes$: Observable<Map<number, PrizeDetails>>;
  currentEventSchedule: EventSchedule | null | undefined;
  user$: Observable<CurrentUser | null | undefined>;
  leaderBoard$: Observable<Leader[]> = new Observable();
  leaderBoardLoading$: Observable<boolean> = new Observable();
  playersTotal$: Observable<number> = new Observable();
  userLoaded$: Observable<boolean> = new Observable();
  eventInstanceUuid: string;
  bestScores$: Observable<BestScores> = new Observable();
  bestScores: BestScores;
  timesPlayed$: Observable<number> = new Observable();
  timesPlayed: number;
  leaderBoardLoaded$: Observable<boolean> = new Observable();
  totalLeaderBoard$: Observable<number> = new Observable();
  private leptRequestsMade = false;
  playersLoaded$: Observable<boolean>;
  private scoresRequestMade = false;
  tournamentType: string;
  userLoaded: boolean;

  // Passes
  passForTournament: UserPasses;

  /**
   * Tournament Passes
   */
  passForTournament$ = this.passesFacade.passes$.pipe(
    withLatestFrom(this.casualTournamentDetails$),
    map(([passes, tournament]) =>
      passes.map((p) => ({
        ...p,

        // in case of tournament is completed, then disable all passes
        ...(!tournament?.isAvailable && {
          disabled: true,
        }),
      })),
    ),
  );
  passForTournamentLoaded$ = this.passesFacade.loaded$;
  activePass$: Observable<TournamentPass | undefined> =
    this.passesFacade.activePass$;

  isLEPT: boolean;
  view$ = this.route.queryParams.pipe(untilDestroyed(this));
  gameHistoryLoaded$: Observable<boolean> =
    this.store.select(getGameHistoryLoaded);
  playButtonStateLoading = toSignal(this.store.select(isPlayReady));

  page = {
    pageIndex: 0,
    pageSize: 10,
  };
  leaderBoardPage = {
    pageIndex: 0,
    pageSize: 10,
  };
  tournamentPasses: ConfiguredPasses[];

  tabsLinks: PageTabRoute[] = [
    {
      path: '',
      name: 'Overview',
      view: 'overview',
    },
    {
      path: '',
      name: 'Open Events',
      view: 'open-events',
    },
    {
      path: '',
      name: 'Details',
      view: 'details',
    },
    {
      path: '',
      name: 'My History',
      view: 'history',
    },
    {
      path: '',
      name: 'Rules',
      view: 'rules',
    },
  ];
  lepTabsLinks: PageTabRoute[] = [
    {
      path: '',
      name: 'Overview',
      view: 'overview',
    },
    {
      path: '',
      name: 'Prizes',
      view: 'details',
    },
    {
      path: '',
      name: 'Rules',
      view: 'rules',
    },
    {
      path: '',
      name: 'My History',
      view: 'history',
    },
    {
      path: '',
      name: 'Leaderboard',
      view: 'leaderboard',
    },
  ];
  view: string;

  private historyRequestMade: boolean;
  titleUpdated = false;
  showScores = false;
  hammer = new Hammer(document.body);
  leaderBoard: Leader[];
  gameHistory: MatchResultsForUser[];

  isPlayStarting = this.#playFacade.isPlayStarting;
  isBuyingPass = this.passesFacade.isBuyingTournamentPass;

  isAuthenticated = toSignal(this.#authService.isLoggedIn());

  ep = toSignal(this.casualTournamentDetails$);
  prototypeId = computed<string>(() => {
    return this.ep()?.eventPrototypeUuid as string;
  });
  scheduleId = signal<string>(this.route.snapshot.params.eventId);

  signInUrl = `/auth/sign-in?redirectTo=${window.location.href}`;

  enabled = computed(() => {
    return !!this.prototypeId() && !!this.scheduleId();
  });
  queryNextEvents = nextEventsQuery(
    {
      prototypeId: this.prototypeId,
      scheduleId: this.scheduleId,
    },
    {
      enabled: this.enabled,
    },
  );

  /**
   * A computed property that returns the next event from the list of upcoming events.
   *
   * @returns {Event | null} The next event if available, otherwise null.
   */
  nextEvent = computed(() => {
    const events = this.queryNextEvents.data();

    if (events?.length) {
      return events[0];
    }

    return null;
  });

  details = toSignal(this.casualTournamentDetails$);
  tournament = toSignal(this.casualTournamentDetails$);

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private store: Store<LeaderState>,
    private tournamentApiService: TournamentApiService,
    private dialog: MatDialog,
    private matchMakingService: MatchMakingService,
    private gtmService: FzGoogleTagManagerService,
    private shareService: ShareService,
    private breadcrumbService: BreadcrumbsService,
    private passesFacade: PassesFacade,
    private title: Title,
    private playTournamentFacade: PlayTournamentFacade,
  ) {}

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.store.dispatch(clearGameHistory());
    this.store.dispatch(clearPasses());
    this.hammer.destroy();
  }

  ngOnInit(): void {
    this.getRouterParams();
    this.intPageView();

    this.hammer.on('swipeleft swiperight', (event) => {
      let route;

      if (event.type === 'swipeleft') {
        route = this.getNextRouteNavigation();
      } else if (event.type === 'swiperight') {
        route = this.getPrevRouteNavigation();
      }

      if (route) {
        this.router.navigate([route.path], {
          queryParams: { view: route.view },
          queryParamsHandling: 'merge',
        });
      }
    });

    // restore scroll position
    setTimeout(() => {
      console.info('Scrolling to top');
      window.scrollTo(0, 0);
    }, 500);
  }

  private getDefaultImage(
    game: Game,
    type: 'avatar' | 'background' = 'avatar',
  ): string {
    const result = game?.key.split('.');
    const [gameName, version] = result || ['default', 'v1'];

    if (type === 'background') {
      return `${env.baseURL}/shared/img/games/${gameName}/${version}/${gameName}-background.webp`;
    }

    return `${env.baseURL}/shared/img/games/${gameName}/${version}/${gameName}-${type}.webp`;
  }

  private getPrevRouteNavigation(): PageTabRoute | void {
    const index = this.getActiveTab();

    if (index > 0) {
      return this.tabsLinks[index - 1];
    }
  }

  private getNextRouteNavigation(): PageTabRoute | void {
    const index = this.getActiveTab();

    if (index < this.tabsLinks.length - 1) {
      return this.tabsLinks[index + 1];
    }
  }

  intPageView() {
    this.view$.subscribe((params) => {
      const view = this.tabsLinks.find((t) => t.view === params.view);
      this.view = view?.view ?? 'overview';
    });

    // check if user has been convert in zbo
    const params = this.route.snapshot.queryParams;
    //
    if (params.zboSubscriptionEnabled) {
      // show modal dialog
      this.dialog.open(ModalAlertComponent, {
        data: {
          modalType: 'SUCCESS_ZBO',
          currentUser: this.user,
          tournament: this.tournamentDetails,
        },
      });
    }
  }

  private updatePageTitle() {
    const tournamentName = this.tournamentDetails?.name;
    const gameName = this.tournamentDetails?.game.name;
    if (tournamentName && !this.titleUpdated) {
      this.titleUpdated = true;
      this.title.setTitle(
        `${this.tournamentType} - ${gameName} - ${tournamentName}`,
      );
    }
  }
  getLeaderBoard() {
    this.leaderBoard$ = this.store
      .select(getAllLeaderBoard)
      .pipe(untilDestroyed(this));
    this.leaderBoard$.subscribe((leaders) => {
      this.leaderBoard = cloneDeep(leaders);
    });
    this.leaderBoardLoading$ = this.store
      .select(getLeaderBoardLoading)
      .pipe(untilDestroyed(this));
    this.totalLeaderBoard$ = this.store
      .select(getTotalLeaderBoard)
      .pipe(untilDestroyed(this));
    this.leaderBoardLoaded$ = this.store
      .select(getLeaderBoardLoaded)
      .pipe(untilDestroyed(this));
  }
  getRouterParams() {
    this.#authService
      .isLoggedIn()
      .pipe(untilDestroyed(this))
      .subscribe((isLoggedIn) => {
        const params = this.route.snapshot.params;
        this.tournamentType = this.route.snapshot.data.tournamentType;

        this.tournamentId = params.tournamentId;
        this.eventId = params.eventId;

        const queryParams = this.route.snapshot.root.queryParams;
        if (queryParams) {
          this.view = queryParams.view || 'overview';
          if (queryParams.pageIndex) {
            this.page.pageIndex = queryParams.pageIndex;
          }
          if (queryParams.pageSize) {
            this.page.pageSize = queryParams.pageSize;
          }
        }
        if (this.tournamentId) {
          this.loggedIn = isLoggedIn;
          if (this.tournamentType === 'prizes') {
            this.tabsLinks = this.getTabs(this.lepTabsLinks, queryParams);
          } else {
            this.tabsLinks = this.getTabs(this.tabsLinks, queryParams);
          }

          this.store.dispatch(
            fromCasualTournamentDetails.loadCasualTournamentDetails({
              url: this.tournamentId,
              eventId: this.eventId,
            }),
          );
        }

        this.getGameHistory();
        this.getCasualTournamentDetails();

        this.getCurrentUser();
        this.getLeaderBoard();
      });
  }

  getTabs(tabsLinks: PageTabRoute[], queryParams: Params) {
    return tabsLinks.map((t) => {
      t.path = `/${this.tournamentType}/${this.tournamentId}/${this.eventId}`;

      if (queryParams.view === t.view) {
        t.active = true;
      }
      return t;
    });
  }

  getActiveTab() {
    return this.tabsLinks.indexOf(
      this.tabsLinks.filter((tab) => tab.view === this.view)[0],
    );
  }

  getGameHistory() {
    this.gameHistory$ = this.store.select(getAllGameHistory).pipe(
      filter((details) => details !== null),
      untilDestroyed(this),
    );

    this.totalGameHistory$ = this.store.select(getGameHistoryTotal).pipe(
      filter((details) => details != null),
      untilDestroyed(this),
    );
    this.gameHistory$.subscribe((gameHistory) => {
      this.gameHistory = cloneDeep(gameHistory);
    });
  }

  getCurrentUser() {
    this.userLoaded$ = this.store.select(fromAuth.getUserLoaded).pipe(
      filter((user) => user != null),
      untilDestroyed(this),
    );
    this.userLoaded$.subscribe((loaded) => {
      this.userLoaded = loaded;
    });

    this.user$ = this.store.select(fromAuth.getCurrentUser).pipe(
      filter((user) => user != null),
      untilDestroyed(this),
    );
    this.user$.subscribe((user) => {
      this.user = user;
    });
  }
  getCasualTournamentDetails() {
    this.store
      .select(fromCasualTournamentDetails.getCasualTournamentDetailsError)
      .pipe(
        filter((details) => details !== null),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.redirectToParent();
      });

    this.currentEventSchedule$ = this.store
      .select(fromCasualTournamentDetails.getEventSchedule(this.eventId))
      .pipe(untilDestroyed(this));

    this.eventSchedulePrizes$ = this.store
      .select(fromCasualTournamentDetails.getEventSchedulePrizes(this.eventId))
      .pipe(untilDestroyed(this));

    this.currentEventSchedule$.subscribe((event) => {
      this.currentEventSchedule = event;
    });

    this.casualTournamentDetails$.subscribe((details) => {
      const event = details ? this.getRoomById(details) : null;
      if (details && event) {
        const isCompleted = details.isCompleted || false;
        this.showScores =
          environment.showScoresWhileTournamentIsInProgress || isCompleted;

        this.tournamentDetails = details;
        this.updatePageTitle();

        if (this.loggedIn) {
          this.checkSubscription();

          this.requestLeptDls(details);

          if (!this.scoresRequestMade) {
            // TODO: it should be for all tournaments types
            this.getBestScores({
              eventPrototypeUuid: details.eventPrototypeUuid,
              eventScheduleUuid: this.eventId,
              globalScore: true,
            });
            if (this.isLEPT) {
              this.getTimesPlayed({
                eventPrototypeUuid: details.eventPrototypeUuid,
                eventScheduleUuid: this.eventId,
              });
            }
            this.scoresRequestMade = true;
          }
        }

        this.breadcrumbService.updateLabel('tournamentName', details.name);

        if (!details.lifeExperience) {
          this.breadcrumbService.updateLabel(
            'entryFee',
            `${this.#currency.convertDollar(
              this.currentEventSchedule?.entryAmount,
            )} USD`,
          );
        }
        if (this.loggedIn && !this.historyRequestMade) {
          this.loadRoomHistory(this.eventId);
          this.historyRequestMade = true;
        }
      } else {
        this.redirectToParent();
      }
    });
  }
  private requestLeptDls(details: CasualTournamentDetails) {
    if (details.lifeExperience && !this.leptRequestsMade) {
      this.isLEPT = true;
      if (details.isForceFinalize) {
        const index = this.tabsLinks.findIndex((t) => t.view === 'leaderboard');
        this.tabsLinks.splice(index, 1);
        if (this.view === 'leaderboard') {
          this.view = 'overview';
        }
      } else {
        this.store.dispatch(
          loadLeaderBoard({
            eventPrototypeUuid: details.eventPrototypeUuid,
            eventScheduleUuid: this.eventId,
            username: this.user?.user.username as string,
            pageIndex: this.leaderBoardPage.pageIndex,
            pageSize: this.leaderBoardPage.pageSize,
          }),
        );
      }

      this.leptRequestsMade = true;
    }
  }

  private getRoomById(details: CasualTournamentDetails): EventSchedule | null {
    if (details?.detailsForEventSchedule) {
      const event = details.detailsForEventSchedule?.find(
        (es: EventSchedule) => es.eventScheduleUuid === this.eventId,
      );
      return event || null;
    }
    return null;
  }
  private redirectToParent() {
    const tag =
      this.tournamentType === 'prizes'
        ? TournamentTagsEnum.PRIZE_TOURNAMENT
        : TournamentTagsEnum.CASH_TOURNAMENT;
    this.router.navigate([`/`], {
      relativeTo: this.route.parent,
      queryParams: { filterbytags: tag.toLowerCase().replace(/ /g, '-') },
      queryParamsHandling: 'merge',
    });
  }

  checkSubscription() {
    if (
      this.tournamentDetails?.lifeExperience &&
      this.tournamentDetails?.comingSoon &&
      this.tournamentDetails?.detailsForEventSchedule[0].subscriptionStatus ===
        undefined
    ) {
      this.store.dispatch(
        loadSubscriptionTournament({
          eventPrototypeUuid: this.tournamentDetails.eventPrototypeUuid,
          eventSchedule: this.tournamentDetails.detailsForEventSchedule[0],
        }),
      );
    }
  }

  getTournamentPasses() {
    if (
      this.tournamentDetails?.lifeExperience &&
      this.tournamentDetails?.detailsForEventSchedule[0]
    ) {
      this.passesFacade.load({
        eventPrototypeUuid: this.tournamentDetails.eventPrototypeUuid,
        eventScheduleUuid:
          this.tournamentDetails.detailsForEventSchedule[0].eventScheduleUuid,
        userId: this.#authService.getUserId(), // TODO: Move this service param for service
      });
    }
  }

  buyPassForTournament(request: BuyPassRequest, options?: { playOnBuy: any }) {
    // TODO: Refactor this function to move to ngrx store
    //
    // Step 1: Check if user is logged in
    //
    if (!this.loggedIn) {
      this.openErrorDialog(
        'login-error',
        this.tournamentDetails,
        'You need to Log In to buy a pass.',
      );
      return;
    }

    //
    // Step 2: Check if tournament is for only ZBO
    //
    if (
      this.tournamentDetails?.zboOnly &&
      this.user &&
      this.user.membershipInfo &&
      this.user.membershipInfo.type &&
      this.user.membershipInfo.type.toLowerCase() !== 'zbo'
    ) {
      this.openErrorDialog('ZBO_ONLY');
      return;
    }

    //
    // Step 3: Check if user has enough balance
    //
    const isFree = request.price === 0;

    if (!isFree && Number(this.user?.user.balance) < request.price) {
      this.openErrorDialog('WALLET_BALANCE');
      return;
    }

    this.passesFacade.buyPass(request, {
      playOnBuy: {
        ...options?.playOnBuy,
        openTournament: this.tournamentDetails,
        entryAmount: this.currentEventSchedule?.entryAmount as number,
        eventSchedule: this.currentEventSchedule,
        game: this.tournamentDetails?.game,
        zboOnly: this.tournamentDetails?.zboOnly,
      },
    });
  }

  getBestScores(params: {
    eventPrototypeUuid: string;
    eventScheduleUuid: string;
    globalScore?: boolean;
  }) {
    this.bestScores$ = this.tournamentApiService
      .getBestScorePlayer(params)
      .pipe(untilDestroyed(this));
    this.bestScores$.subscribe((scores) => {
      this.bestScores = scores;
    });
  }

  getTimesPlayed(params: {
    eventPrototypeUuid: string;
    eventScheduleUuid: string;
  }) {
    this.store.dispatch(
      fromCasualTournamentDetails.timesUserTournamentPlayed({
        eventPrototypeUuid: params.eventPrototypeUuid,
        eventScheduleUuid: params.eventScheduleUuid,
      }),
    );
  }

  loadRoomHistory(eventScheduleUuid: string) {
    this.store.dispatch(
      loadGameHistory({
        params: {
          forfeited: false,
          gameType: 'CASUAL',
          orderBy: 'createdAt',
          orderDir: 'DESC',
          pageIndex: this.page.pageIndex,
          pageSize: this.page.pageSize,
          eventScheduleUuid: eventScheduleUuid,
          eventPrototypeUuid: this.tournamentDetails?.eventPrototypeUuid,
        },
      }),
    );
  }

  /**
   * TODO: Remove this function
   * @deprecated
   */
  openMatchMakingDialog(pass?: TournamentPass | null | undefined) {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      user: this.user,
      openTournament: this.tournamentDetails,
      scheduleSelected: this.currentEventSchedule,
      pass: pass,
    };

    const dialogRef = this.dialog.open(
      MatchMakingDialogComponent,
      dialogConfig,
    );

    dialogRef.afterClosed().subscribe((successResponse) => {
      if (
        successResponse &&
        (successResponse === 'redirectToRoom' ||
          successResponse === 'refreshPassInfo')
      ) {
        this.getTournamentPasses();
        return;
      }
      if (successResponse) {
        this.matchMakingService.playCasualTournament(successResponse);
      }
    });
  }

  openErrorDialog(
    errorType: string,
    tournament?: CasualTournamentDetails | null,
    title?: string,
  ) {
    this.dialog.open(ModalAlertComponent, {
      data: {
        modalType: errorType,
        currentUser: this.user,
        tournament,
        title,
      },
    });
  }

  onPlay(activePass?: TournamentPass | null | undefined) {
    console.log('[Casual Tournaments Play]', activePass);
    if (activePass && !activePass?.purchased) {
      this.buyPassForTournament(
        {
          eventPrototypeUuid: this.tournamentDetails
            ?.eventPrototypeUuid as string,
          tournamentName: this.tournamentDetails?.name as string,
          gameName: this.tournamentDetails?.game.name as string,
          fantazPassUuid: activePass.uuid,
          eventScheduleUuid: this.currentEventSchedule?.uuid as string,
          promoCodes: activePass.price.promoCodes,
          price: Number(activePass.price.price),
          passIndex: activePass.passIndex,
        },
        {
          playOnBuy: {
            openTournament: this.tournamentDetails,
            entryAmount: this.currentEventSchedule?.entryAmount as number,
            eventSchedule: this.currentEventSchedule,
            game: this.tournamentDetails?.game,
            zboOnly: this.tournamentDetails?.zboOnly,
          },
        },
      );
    } else {
      if (this.tournamentDetails && this.currentEventSchedule) {
        this.playTournamentFacade.playTournament({
          openTournament: this.tournamentDetails,
          pass: activePass,
          entryAmount: this.currentEventSchedule?.entryAmount as number,
          eventSchedule: this.currentEventSchedule,
          game: this.tournamentDetails?.game,
          zboOnly: this.tournamentDetails?.zboOnly,
          eventCode: this.nextEvent()?.eventCode,
        });
      }
    }
  }

  onLogin() {
    window.location.href = '/auth/sign-in';
  }

  onTryForFree() {
    this.playTournamentFacade.tryForFreeTournament({
      name: this.tournamentDetails?.name as string,
      game: this.tournamentDetails?.game as Game,
    });
  }

  /**
   *
   * @deprecated
   */
  private async sendGAEvent(event: GoogleAnalyticsEvent): Promise<unknown> {
    return this.gtmService.pushTag(event);
  }

  onShare() {
    console.log(
      '[Casual Tournaments Share Tournament]',
      this.tournamentDetails,
    );
    // TODO: Move to TournamentsFacade and refactor share
    // build share url
    if (this.tournamentDetails) {
      const pageUrl = window.location.href.split('?')[0];
      let urlStr = `${pageUrl}?view=${this.view}`;

      if (
        this.tournamentDetails.lifeExperience &&
        this.tournamentDetails.metadata?.vip === 'true'
      ) {
        urlStr = `${window.location.origin}/vip/${this.tournamentDetails.url}`;
      }
      const url = new URL(urlStr);
      this.shareService.share({
        title: 'FantaZ',
        text: 'Play Casual tournaments',
        url: url.toString(),
        currentUser: this.user,
      });
    }
  }

  onSharePass(pass: TournamentPass) {
    const url = new URL(window.location.href);

    if (pass.price?.promoCodes?.length) {
      url.searchParams.set(
        'promocode',
        pass.price?.promoCodes[0].uuid as string,
      );
    }

    console.log('[Casual Tournaments Share Pass]', pass), url;

    this.shareService.share({
      title: 'FantaZ',
      text: 'Play Casual tournaments',
      url: url.toString(),
      currentUser: this.user,
    });
  }

  onGameHistoryPageChange(pageInfo: { pageIndex: number; pageSize: number }) {
    this.page = pageInfo;

    this.store.dispatch(
      loadGameHistory({
        params: {
          forfeited: false,
          gameType: 'CASUAL',
          orderBy: 'createdAt',
          orderDir: 'DESC',
          pageIndex: pageInfo.pageIndex,
          pageSize: pageInfo.pageSize,
          eventScheduleUuid: this.currentEventSchedule?.eventScheduleUuid,
          eventPrototypeUuid: this.tournamentDetails?.eventPrototypeUuid,
        },
      }),
    );
  }
  onLeaderboardPageChange(pageInfo: { pageIndex: number; pageSize: number }) {
    this.leaderBoardPage = pageInfo;
    if (this.tournamentDetails) {
      this.store.dispatch(
        loadLeaderBoard({
          eventPrototypeUuid: this.tournamentDetails?.eventPrototypeUuid,
          eventScheduleUuid: this.eventId,
          username: this.user?.user.username as string,
          pageIndex: this.leaderBoardPage.pageIndex,
          pageSize: this.leaderBoardPage.pageSize,
        }),
      );
    }
  }

  onViewGameHistory(element: MatchResultsForUser) {
    this.router.navigate([`/gamehistory/view/${element.playInstanceUuid}`]);
  }

  onSelectedTabIndex(event: number) {
    this.selectedTab = event;
  }
}
